import React from 'react';
import { Form, Input, Select, Typography, InputNumber, Row, Col, Divider, AutoComplete } from 'antd';

const { Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const WindowFunctionNodeProperties = ({ config, inputSchema }) => {
  // Watch the windowFunction field for conditional rendering
  const windowFunction = Form.useWatch('windowFunction');
  const currentWindowFunction = windowFunction || config.windowFunction || 'row_number';

  // Functions that require an operation column
  const requiresOperationColumn = ['lag', 'lead', 'sum', 'avg', 'min', 'max', 'first_value', 'last_value', 'count'];
  // Functions that support offset (lag/lead)
  const supportsOffset = ['lag', 'lead'];
  // Custom expression mode
  const isCustomMode = currentWindowFunction === 'custom';

  return (
    <>
      <Form.Item label="Window Function" name="windowFunction" initialValue={config.windowFunction || 'row_number'} rules={[{ required: true }]}>
        <Select>
          <Option value="row_number">Row Number</Option>
          <Option value="rank">Rank</Option>
          <Option value="dense_rank">Dense Rank</Option>
          <Option value="lag">Lag (Previous Value)</Option>
          <Option value="lead">Lead (Next Value)</Option>
          <Option value="first_value">First Value</Option>
          <Option value="last_value">Last Value</Option>
          <Option value="sum">Running Sum</Option>
          <Option value="avg">Running Average</Option>
          <Option value="min">Running Min</Option>
          <Option value="max">Running Max</Option>
          <Option value="count">Running Count</Option>
          <Option value="custom">✨ Custom Polars Expression</Option>
        </Select>
      </Form.Item>

      <Form.Item 
        label="Result Column Name" 
        name="resultColumn" 
        initialValue={config.resultColumn || 'window_result'} 
        rules={[{ required: true }]}
        extra={
          <Text type="secondary" style={{ fontSize: 11 }}>
            Select existing column to overwrite, or type new name to create
          </Text>
        }
      >
        <AutoComplete
          placeholder="Select or create column name"
          options={inputSchema?.map(col => ({ value: col.name, label: col.name }))}
          filterOption={(inputValue, option) =>
            option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
          }
        />
      </Form.Item>

      {/* Custom Polars Expression */}
      {isCustomMode && (
        <>
          <Form.Item 
            label="Polars Expression" 
            name="customExpression" 
            initialValue={config.customExpression || ''} 
            rules={[{ required: true, message: 'Please enter a Polars expression' }]}
            extra={
              <Text type="secondary" style={{ fontSize: 11 }}>
                Use <code>col("column_name")</code> to reference columns
              </Text>
            }
          >
            <TextArea 
              placeholder='e.g. col("price").cumSum() or col("value").diff()'
              autoSize={{ minRows: 2, maxRows: 6 }}
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>

          <div style={{ padding: '8px 12px', backgroundColor: '#f0f5ff', borderRadius: '4px', fontSize: '11px', marginBottom: 12, border: '1px solid #d6e4ff' }}>
            <Text strong style={{ fontSize: 12 }}>Polars Expression Examples:</Text>
            <ul style={{ paddingLeft: 16, margin: '4px 0 0 0' }}>
              <li><code>col("price").cumSum()</code> - Running sum</li>
              <li><code>col("value").diff()</code> - Difference from previous</li>
              <li><code>col("qty").shift(1)</code> - Lag by 1 (previous row)</li>
              <li><code>col("qty").shift(-1)</code> - Lead by 1 (next row)</li>
              <li><code>col("score").rank("dense")</code> - Dense rank</li>
              <li><code>col("price").rollingMean(3)</code> - 3-period moving avg</li>
              <li><code>col("value").cumMax()</code> - Cumulative max</li>
              <li><code>col("a") - col("b").shift(1)</code> - Difference from prev</li>
            </ul>
            <Text strong style={{ fontSize: 12, marginTop: 8, display: 'block' }}>Conditional (when/then/otherwise):</Text>
            <ul style={{ paddingLeft: 16, margin: '4px 0 0 0' }}>
              <li><code>when(col("a").eq(col("a").shift(1))).then(col("b").shift(1)).otherwise(col("b"))</code></li>
              <li><code>when(col("x").gt(100)).then(lit("high")).otherwise(lit("low"))</code></li>
            </ul>
            <Text type="warning" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
              ⚠️ Only write column expression, NOT <code>df.with_columns(...)</code>
            </Text>
          </div>
        </>
      )}

      {requiresOperationColumn.includes(currentWindowFunction) && (
        <Form.Item label="Column to Operate On" name="operationColumn" initialValue={config.operationColumn || ''} rules={[{ required: true }]}>
          {inputSchema && inputSchema.length > 0 ? (
            <Select placeholder="Column name for the window operation" showSearch>
              {inputSchema.map(col => (
                <Option key={col.name} value={col.name}>{col.name}</Option>
              ))}
            </Select>
          ) : (
            <Input placeholder="Column name for the window operation" />
          )}
        </Form.Item>
      )}

      {supportsOffset.includes(currentWindowFunction) && (
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label="Offset" name="offset" initialValue={config.offset || 1}>
              <InputNumber min={1} style={{ width: '100%' }} placeholder="Number of rows" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Default Value" name="defaultValue" initialValue={config.defaultValue || null}>
              <Input placeholder="Value when offset is out of bounds" />
            </Form.Item>
          </Col>
        </Row>
      )}

      <Divider style={{ margin: '12px 0' }} />

      <Form.Item label="Partition By" name="partitionBy" initialValue={config.partitionBy || []}>
        <Select mode="tags" placeholder="Columns to partition by (optional)">
          {inputSchema && inputSchema.map(col => (
            <Option key={col.name} value={col.name}>{col.name}</Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item label="Order By" name="orderBy" initialValue={config.orderBy || []}>
        <Select mode="tags" placeholder="Columns to order by (optional)">
          {inputSchema && inputSchema.map(col => (
            <Option key={col.name} value={col.name}>{col.name}</Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item label="Order Direction" name="orderDirection" initialValue={config.orderDirection || 'asc'}>
        <Select>
          <Option value="asc">Ascending</Option>
          <Option value="desc">Descending</Option>
        </Select>
      </Form.Item>

      <div style={{ padding: '8px 12px', backgroundColor: '#f9f9f9', borderRadius: '4px', fontSize: '12px', marginTop: '12px' }}>
        <Text type="secondary">
          <strong>Window Function Node:</strong><br/>
          • Performs calculations across related rows<br/>
          • <strong>Partition By:</strong> Groups rows for separate calculations<br/>
          • <strong>Order By:</strong> Defines row order within partitions<br/>
          • <strong>Custom Mode:</strong> Write raw Polars expressions for advanced use cases
        </Text>
      </div>
    </>
  );
};

export default WindowFunctionNodeProperties;